import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import {
  first,
  get,
  isEmpty,
  map,
  pipe,
  slice,
  flatMap,
  noop,
  isNil,
  update,
  find,
} from 'lodash/fp';
import {
  Avatar,
  Menu,
  Placeholder,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuList,
  DropdownMenuItem,
  UtilityButton,
} from '@kinesis/bungle';
import { Content } from '@kinesis/bungle/legacy';
import {
  hasEntries,
  entries,
  keyOf,
  labelOf,
  addItemValueToInstantiationWithCategory,
} from 'data/attributes';
import loadedAppsSelector from 'selectors/loadedAppsSelector';
import Layout from 'components/layout';
import { useSelectorWithProps } from 'hooks';
import { ToolboxSection } from 'components/toolbox/toolbox-section';
import FormEntry from './form-entry';
import {
  AttributeSettingsToolbox,
  Description,
} from './attribute-settings.styles';

const propTypes = {
  addItem: PropTypes.func, // FUTURE: .isRequired
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node).isRequired,
  ]),
  description: PropTypes.string.isRequired,
  errors: PropTypes.array,
  hasSettings: PropTypes.bool,
  instantiation: PropTypes.object.isRequired,
  itemAddition: PropTypes.bool,
  selections: PropTypes.array,
  setSelections: PropTypes.func,
  workspaceId: PropTypes.number.isRequired,
  setInstantiation: PropTypes.func,
  setSetting: PropTypes.func,
  setIsHeaderInFocus: PropTypes.func,
};

const defaultProps = {
  addItem: undefined,
  children: null,
  errors: [],
  hasSettings: true,
  itemAddition: false,
  selections: undefined,
  setSelections: undefined,
  setInstantiation: undefined,
  setSetting: undefined,
  setIsHeaderInFocus: noop,
};

const GenericSettings = ({
  addItem,
  children,
  description,
  errors,
  hasSettings,
  instantiation,
  itemAddition,
  selections,
  setSelections,
  workspaceId,
  setSetting,
  setInstantiation,
  setIsHeaderInFocus,
}) => {
  const items = useMemo(() => entries(instantiation), [instantiation]);
  const apps = useSelectorWithProps(loadedAppsSelector, { workspaceId });
  useEffect(() => {
    if (isEmpty(selections) && hasSettings) {
      setSelections([
        keyOf(first(flatMap((e) => (hasEntries(e) ? entries(e) : [e]), items))),
      ]);
    }
  }, [items, selections, hasSettings, setSelections]);
  const allowMultiSelect = false;
  const onClick = (item) => (e) => {
    // NOTE: this is important, event (e) can not be referenced in async set call.
    const { shiftKey } = e;
    setSelections((current) =>
      shiftKey && allowMultiSelect ? [...current, keyOf(item)] : [keyOf(item)],
    );
  };
  const variant = (item) => {
    if (selections.includes(keyOf(item))) {
      return 'accent';
    }
    if (map(get('categoryKey'), errors).includes(keyOf(item))) {
      return 'danger';
    }
    return undefined;
  };

  const defaultLabel = (item) =>
    get('entries', item) ? 'New category' : 'New item';

  const onDuplicateItem = (item, category = undefined) => {
    if (!setSetting || !setInstantiation) return;
    const key = `${item.key}-${nanoid()}`;
    const duplicate = isNil(category)
      ? {
          ...item,
          key,
          label: `${item.label} (Duplicate)`,
          entries: item.entries.map((e) => ({
            ...e,
            key: `${e.key}-${nanoid()}`,
          })),
        }
      : { ...item, key, label: `${item.label} (Duplicate)` };
    setSetting((current) => {
      const fields = !isNil(category)
        ? pipe(
            get('categories'),
            find((v) => v.key === category),
            get('entries'),
            find((v) => v.key === item.key),
            get('fields'),
          )(current)
        : pipe(
            get('categories'),
            find((v) => v.key === item.key),
            get('fields'),
          )(current);
      const unit = !isNil(category)
        ? pipe(
            get('categories'),
            find((v) => v.key === category),
            get('entries'),
            find((v) => v.key === item.key),
            get('unit'),
          )(current)
        : pipe(
            get('categories'),
            find((v) => v.key === item.key),
            get('unit'),
          )(current);
      const duplicateSetting = {
        ...duplicate,
        fields,
        unit,
        item_type: undefined,
        newItem: true,
      };
      const result = !isNil(category)
        ? update(
            'categories',
            map((c) =>
              c.key === category
                ? update('entries', (v) => v.concat([duplicateSetting]), c)
                : c,
            ),
            current,
          )
        : update('categories', (v) => v.concat([duplicateSetting]), current);
      return result;
    });

    if (!isNil(category)) {
      setInstantiation((current) =>
        addItemValueToInstantiationWithCategory(category, current, duplicate),
      );
    }
    setSelections([key]);
    setIsHeaderInFocus(true);
    return false;
  };

  const menuItem = (item, category = undefined) => (
    <Menu.Item
      header={hasEntries(item)}
      variant={variant(item)}
      key={keyOf(item)}
      content={isEmpty(get('label', item)) ? defaultLabel(item) : labelOf(item)}
      onClick={onClick(item)}
      actions={
        setSetting && setInstantiation && !hasEntries(item)
          ? [
              <DropdownMenu justify='end' placeholder='bottom'>
                <DropdownMenuButton
                  as={UtilityButton}
                  icon='ellipsis'
                  magnitude='small'
                  stopPropagation
                  preventDefault
                />
                <DropdownMenuList offset={8}>
                  <DropdownMenuItem
                    stopPropagation
                    preventDefault
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateItem(item, category);
                    }}
                  >
                    Duplicate
                  </DropdownMenuItem>
                </DropdownMenuList>
              </DropdownMenu>,
            ]
          : undefined
      }
    >
      {hasEntries(item) && (
        <>
          {map((i) => menuItem(i, item.key), entries(item))}
          {itemAddition && (
            <Menu.Item
              icon='plus'
              key={`add-item-${keyOf(item)}`}
              content='Add item'
              onClick={addItem(keyOf(item))}
            />
          )}
        </>
      )}
    </Menu.Item>
  );

  return (
    <Layout direction='row'>
      <AttributeSettingsToolbox arrangement='left' width={304}>
        <ToolboxSection collapsible title='Description'>
          <Description>{description}</Description>
        </ToolboxSection>
        {hasSettings && (
          <ToolboxSection collapsible title='Items'>
            <Menu>{map(menuItem, items)}</Menu>
          </ToolboxSection>
        )}
        <ToolboxSection collapsible title='Used by'>
          <Menu>
            {map((app) => (
              <Menu.Item
                key={app.name}
                content={app.label}
                icon={
                  <Avatar
                    alt={`${app.publisher} logo`}
                    image={`data:image/png;base64,${app.image}`}
                    magnitude='xsmall'
                    variant='organisation'
                  >
                    {pipe(get('publisher'), slice(0, 1))(app)}
                  </Avatar>
                }
              />
            ))(apps)}
          </Menu>
        </ToolboxSection>
      </AttributeSettingsToolbox>
      <Layout direction='column'>
        {isEmpty(selections) && hasSettings && (
          <Content alignX='center' alignY='center'>
            <Placeholder>
              Select a category or item to view settings.
            </Placeholder>
          </Content>
        )}
        {/* FUTURE: we should be able to data-drive this from the setting, for
            now we are coding up the specific settings bodies. */}
        {!isEmpty(selections) && children}
        {!hasSettings && (
          <FormEntry label='Settings'>
            <Content padding='none'>
              <Placeholder>
                There are no settings for this attribute.
              </Placeholder>
            </Content>
          </FormEntry>
        )}
      </Layout>
    </Layout>
  );
};

GenericSettings.defaultProps = defaultProps;
GenericSettings.propTypes = propTypes;

export default GenericSettings;
